/**
 * MacroProgress — a horizontal hairline progress bar with the macro number
 * as the dominant element (not the bar). Bar is decoration; the number is the data.
 * Color coding:
 *   default: moss when in range, persimmon when over target
 *   warn (sugar cap): persimmon when over cap, moss/muted when under
 */
export function MacroProgress({
  label,
  current,
  target,
  unit,
  tone = "default",
}: {
  label: string
  current: number
  target: number
  unit: string
  tone?: "default" | "warn"
}) {
  const pct = target > 0 ? Math.min(150, (current / target) * 100) : 0
  const over = current > target
  const barColor =
    tone === "warn"
      ? over
        ? "bg-persimmon"
        : "bg-muted-foreground/40"
      : over
        ? "bg-persimmon"
        : "bg-moss"
  const remainingLabel = tone === "warn"
    ? over
      ? `${(current - target).toFixed(1)} over cap`
      : `${(target - current).toFixed(0)} ${unit} below cap`
    : over
      ? `${(current - target).toFixed(0)} ${unit} over target`
      : `${(target - current).toFixed(0)} ${unit} left`

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-3">
        <span className="eyebrow">{label}</span>
        <span className="font-mono text-xs text-muted-foreground tabular-nums">
          {current.toFixed(0)} / {target} {unit}
        </span>
      </div>
      <div className="relative h-px bg-border overflow-visible">
        <div
          className={`absolute top-0 left-0 h-px ${barColor} transition-all duration-500 ease-out`}
          style={{ width: `${Math.min(100, pct)}%` }}
        />
        {/* Over-target tick */}
        {over && (
          <div
            className="absolute -top-px h-[3px] bg-persimmon"
            style={{ left: "100%", width: `${Math.min(50, pct - 100)}%` }}
          />
        )}
      </div>
      <p className="font-sans text-xs text-muted-foreground">{remainingLabel}</p>
    </div>
  )
}
